import moment from 'moment-timezone'
import { isEqual } from 'lodash'
import type { LocationQuery, LocationQueryRaw, LocationQueryValue, RouteLocationRaw } from 'vue-router'

/**
 * A list's view in its URL query (AdminScreenTypes §3.3): the applied filters, the text search,
 * the page, the page size and the sort — so a reload or a shared link opens the same view, and an
 * editor's Nazad / Sačuvaj returns to it. Only what differs from the list's defaults is written;
 * a value the list cannot read (unknown, malformed) is ignored and its default applies.
 *
 *   ?startDate=2026-09-01&endDate=2026-09-30&client=12&search=mleko&page=2&perPage=100&sort=-number
 *
 * Filters keep their own names and their wire form ('YYYY-MM-DD' dates, 'HH:mm:ss' times, ids);
 * `search`, `page`, `perPage` and `sort` (the wire field, `-` first = descending) are the table's.
 *
 * Tested by test/listQuery.spec.ts (TestingStrategy §3 F2b).
 */

/** One value's URL form: `parse` returns undefined for a value the list cannot read. */
export interface QueryValue<T> {
    parse(raw: string): T | undefined
    format(value: T): string
}

/** The URL form of each filter a list keeps in its query (filters without one stay out of it). */
export type QueryFields<V> = { [K in keyof V]?: QueryValue<NonNullable<V[K]>> }

const DATE_VALUE: QueryValue<string> = {
    parse: raw => (moment(raw, 'YYYY-MM-DD', true).isValid() ? raw : undefined),
    format: value => value
}

/** A value of a fixed spelling (a key, a slug), passed through as written. */
function patternValue(pattern: RegExp): QueryValue<string> {
    return { parse: raw => (pattern.test(raw) ? raw : undefined), format: value => value }
}

export const queryValue = {
    /** 'YYYY-MM-DD' — a real calendar day. */
    date: DATE_VALUE,
    /** 'HH:mm:ss'. */
    time: {
        parse: raw => (moment(raw, 'HH:mm:ss', true).isValid() ? raw : undefined),
        format: value => value
    } as QueryValue<string>,
    /** A record id: a positive whole number. */
    id: {
        parse: raw => (/^[1-9]\d{0,14}$/.test(raw) ? Number(raw) : undefined),
        format: value => String(value)
    } as QueryValue<number>,
    /** A decimal number (a quantity). */
    number: {
        parse: raw => (/^-?\d{1,15}(\.\d{1,8})?$/.test(raw) ? Number(raw) : undefined),
        format: value => String(value)
    } as QueryValue<number>,
    /** Free text the user typed into a filter. */
    text: { parse: raw => (raw.trim() ? raw : undefined), format: value => value } as QueryValue<string>,
    /** An option key (a payment type, a status). */
    key: patternValue(/^[a-z0-9_-]{1,64}$/i),
    /** 'client:<id>' | 'venue:<id>' — a counterparty filter (the morph type rides the value). */
    counterparty: patternValue(/^(client|venue):[1-9]\d{0,14}$/),
    /** One of the listed values. */
    oneOf<T extends string>(values: readonly T[]): QueryValue<T> {
        return {
            parse: raw => values.find(value => value === raw),
            format: value => value
        }
    }
}

/** The first string of a query value (`?key` alone, or a repeated key's later values, do not count). */
export function queryString(value: LocationQueryValue | LocationQueryValue[] | undefined): string | undefined {
    const first = Array.isArray(value) ? value[0] : value
    return typeof first === 'string' ? first : undefined
}

/**
 * The query form of the applied filters: the fields that differ from their defaults. A period's
 * dates go together — one changed date writes every date filter, because a default period moves
 * with today and a link must keep the exact period. A null is never written (no list lets the user
 * clear a filter whose default is set — its default applies again).
 */
export function encodeFilters<V extends object>(values: V, defaults: V, fields: QueryFields<V>): Record<string, string> {
    const keys = Object.keys(fields) as (keyof V & string)[]
    const differs = (key: keyof V) => !isEqual(values[key], defaults[key])
    const dateKeys = keys.filter(key => fields[key] === DATE_VALUE)
    const datesChanged = dateKeys.some(differs)

    const query: Record<string, string> = {}
    for (const key of keys) {
        const value = values[key]
        if (value === null || value === undefined) continue
        if (!differs(key) && !(datesChanged && dateKeys.includes(key))) continue
        query[key] = fields[key]!.format(value as NonNullable<V[typeof key]>)
    }
    return query
}

/** The filters a query holds over the defaults; a field it does not hold or cannot read keeps its default. */
export function decodeFilters<V extends object>(query: LocationQuery, defaults: V, fields: QueryFields<V>): V {
    const values = { ...defaults }
    for (const key of Object.keys(fields) as (keyof V & string)[]) {
        const raw = queryString(query[key])
        if (raw === undefined) continue
        const parsed = fields[key]!.parse(raw)
        if (parsed !== undefined) values[key] = parsed as V[typeof key]
    }
    return values
}

/** The table's part of a list's view (useServerTable's request params). */
export interface TableView {
    page: number
    itemsPerPage: number
    search: string
    sortBy: string | null
    sortDesc: boolean
}

/** What a query may set on the table: the page sizes it offers and the fields it sorts by. */
export interface TableQueryRules {
    itemsPerPageOptions: readonly number[]
    sortFields: readonly string[]
}

/** The table's own keys — a filter never uses them. */
export const TABLE_QUERY_KEYS = ['search', 'page', 'perPage', 'sort'] as const

/** The query form of the table's view: what differs from the defaults (page 1, no search, the list's sort). */
export function encodeTable(view: TableView, defaults: TableView): Record<string, string> {
    const query: Record<string, string> = {}
    if (view.search.trim()) query.search = view.search
    if (view.page > 1) query.page = String(view.page)
    if (view.itemsPerPage !== defaults.itemsPerPage) query.perPage = String(view.itemsPerPage)
    if (view.sortBy && (view.sortBy !== defaults.sortBy || view.sortDesc !== defaults.sortDesc)) {
        query.sort = (view.sortDesc ? '-' : '') + view.sortBy
    }
    return query
}

/** The table's view a query holds over the defaults; a value it cannot read keeps its default. */
export function decodeTable(query: LocationQuery, defaults: TableView, rules: TableQueryRules): TableView {
    const view = { ...defaults }

    const search = queryString(query.search)
    if (search?.trim()) view.search = search

    const page = queryString(query.page)
    if (page && /^[1-9]\d{0,8}$/.test(page)) view.page = Number(page)

    const perPage = Number(queryString(query.perPage))
    if (rules.itemsPerPageOptions.includes(perPage)) view.itemsPerPage = perPage

    const sort = /^(-?)(.+)$/.exec(queryString(query.sort) ?? '')
    if (sort && rules.sortFields.includes(sort[2])) {
        view.sortBy = sort[2]
        view.sortDesc = sort[1] === '-'
    }
    return view
}

/** Two queries say the same (an absent key and an undefined one are the same). */
export function sameQuery(a: LocationQueryRaw, b: LocationQueryRaw): boolean {
    const defined = (query: LocationQueryRaw) =>
        Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined))
    return isEqual(defined(a), defined(b))
}

/** Where a list was left: its path (with any params) and its view's query. */
export interface ListLocation {
    path: string
    query: LocationQueryRaw
}

/**
 * The lists' last locations, by list route name — an editor returns to its list as the user left
 * it (same filters, same page) without knowing the list's query. A list nobody left yet (a deep
 * link straight into the editor) opens at its bare route.
 */
export function createListLocations() {
    const locations = new Map<string, ListLocation>()

    return {
        remember(name: string, location: ListLocation): void {
            locations.set(name, location)
        },
        /** The list as it was left; `extra` adds one-shot params (`open`) that are never remembered. */
        location(name: string, extra: LocationQueryRaw = {}): RouteLocationRaw {
            const left = locations.get(name)
            return left
                ? { path: left.path, query: { ...left.query, ...extra } }
                : { name, query: { ...extra } }
        },
        /** The session ended: the next one starts from the bare lists. */
        forget(): void {
            locations.clear()
        }
    }
}

const listLocations = createListLocations()

export const rememberListLocation = listLocations.remember
/** Where an editor's Nazad (and its leave after Sačuvaj / Obriši / Proknjiži) goes: the list as it was left. */
export const listLocation = listLocations.location
export const forgetListLocations = listLocations.forget
